import {
    any,
    arrayOf,
    bool,
    func,
    instanceOf,
    object,
    objectOf,
    oneOf,
    shape,
    string,
} from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import _forEach from 'lodash/forEach';

import Button from 'ravenjs/lib/Button';
import Checkbox from 'ravenjs/lib/CheckboxNew';
import Col from 'ravenjs/lib/Col';
import DatePicker from 'ravenjs/lib/DatePicker';
import Responsive from 'ravenjs/lib/Responsive';
import Row from 'ravenjs/lib/Row';
import AsyncSelect from 'ravenjs/lib/Select/AsyncSelect';
import Input from 'ravenjs/lib/Input';
import Typography from 'ravenjs/lib/Typography';
import { getHandler } from 'ravenjs/utils/actions';
import { get } from 'ravenjs/utils/lodash';
import { getThemeProps, themeGet } from 'ravenjs/utils/theme';

import ContentTooltipPreview from 'components/ContentTooltipPreview';
import {
    CalendarIcon,
    CheckboxEmptyIcon,
    CheckboxFilledIcon,
    ClosePanelIcon,
    FilterIcon,
    GearIcon,
} from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { FILTER_TYPE } from 'constants/filters';
import { MODALS } from 'constants/modals';
import { actions as uiActions } from 'modules/ui';

import DropdownFilter from './DropdownFilter';
import TypeaheadFilter from './TypeaheadFilter';
import { InputContainer } from './TypeaheadFilter/TypeAheadFilter';

const DatePickerInputContainer = styled.div`
    border-radius: 2px;
    background-color: ${themeGet('palette.common.white')};
    border: solid 1px ${DEFAULT_COLORS.PLACEHOLDER};
    box-shadow: none;
    color: ${themeGet('palette.common.input')};
    display: flex;
    font-size: 0.875rem;
    height: 38px;
    line-height: 1.5;
    padding: 0.513rem;

    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    width: 100%;
    &::placeholder {
        color: ${themeGet('palette.common.placeholder')};
    }

    ${themeGet('effects.inputFocus')};

    &[disabled],
    &[readonly] {
        background-color: #e1e5e9 !important;
        border-color: transparent;
        box-shadow: none;
        cursor: not-allowed;
        user-select: none;
    }

    ${themeGet('Input.styles')};
`;

const FilterItem = styled.div`
    width: 100%;
    min-width: 200px;
    .react-datepicker-wrapper {
        width: 100%;
    }
    .react-datepicker-popper {
        z-index: 3;
    }
    .react-datepicker {
        border-radius: 0;
        box-shadow: 0 9px 11px 0 rgba(0, 0, 0, 0.24);
    }
`;

const FilterToggle = styled.span`
    display: inherit;
    cursor: pointer;
    padding: 0.625rem;
    & span {
        color: ${DEFAULT_COLORS.BLACK};
        font-size: 0.875rem;
        font-weight: 500;
        margin: 0.188rem 0 0;
    }
`;

const FilterContainer = styled(Row)`
    width: 100%;
    padding: 0 0 0.5rem 0;
    ${({ theme }) => theme.media.down('md')`
        margin-top: 0;
    `};
    ${({ theme }) => theme.media.up('md')`
        margin-top: 1.02rem;
    `};
`;

const ResetLink = styled(Typography)`
    &:hover {
        color: ${DEFAULT_COLORS.BLACK};
        text-decoration: none;
    }
`;

const DatePickerInput = styled.input`
    border: none;
    background: transparent;
    padding-left: 5px;
    margin-left: 5px;
    width: 80%;
    margin-top: 2px;
    font-weight: 500;
    color: ${themeGet('palette.common.input')};
    ::placeholder {
        color: ${DEFAULT_COLORS.BLACK};
    }
`;

const IconStyled = styled.div`
    margin-right: 0.625rem;
`;

const FilterRow = styled(Row)`
    background-color: ${({ expanded }) =>
        !expanded ? `${DEFAULT_COLORS.COMPONENTS_THE_GREY_STYLE};` : 'none'};
`;

const MobileFilter = styled.div`
    align-items: center;
    background: ${DEFAULT_COLORS.COMPONENTS_THE_GREY_STYLE};
    border: none;
    border-radius: 0;
    cursor: pointer;
    display: flex;
    height: 2.375rem;
    justify-content: center;
    padding: 0.375rem 1.125rem 0.375rem 0.5rem;
    margin: 1rem 0 0.5rem 0;
    width: 100%;
`;

const ClosePanelContainer = styled.div`
    margin-left: 1.117rem;
    cursor: pointer;
    align-self: flex-end;
`;

const CheckboxContainer = styled.div`
    display: flex;
    height: 38px;
`;

const CheckboxStyled = styled(Checkbox)`
    align-self: center;
    height: 20px;
    width: 16px;
    margin: 0 0.812rem;
    ${({ theme }) => theme.media.down('md')`
        margin: 0 0.812rem 0 0;
    `};
`;

const TitleStyled = styled(Typography)`
    align-self: center;
    margin: 0 0.5rem 0 0;
    line-height: 1;
`;

const InputStyled = styled(Input)`
    border-radius: 0;
    border-width: 0;
    color: ${DEFAULT_COLORS.BLACK};
    font-size: 0.875rem;
    font-weight: 500;
    :focus {
        box-shadow: none;
    }
    color: ${props => (props.disabled ? DEFAULT_COLORS.LIGHT : DEFAULT_COLORS.BLACK)};
    ::placeholder {
        color: ${props => (props.disabled ? DEFAULT_COLORS.LIGHT : DEFAULT_COLORS.PLACEHOLDER)};
    }
`;

const CustomDatePickerInput = React.forwardRef(({ onChange, onClick, placeholder, value }, ref) => {
    return (
        <DatePickerInputContainer onClick={onClick}>
            <CalendarIcon />
            <DatePickerInput
                onChange={onChange}
                placeholder={placeholder}
                ref={ref}
                value={value}
            />
        </DatePickerInputContainer>
    );
});

CustomDatePickerInput.propTypes = {
    onChange: func,
    onClick: func,
    placeholder: string,
    value: string,
};

CustomDatePickerInput.defaultProps = {
    onChange: () => {},
    onClick: () => {},
    placeholder: '',
    value: '',
};

const Filters = ({
    actions,
    checkedItems,
    setCheckedItems,
    filters,
    selectedFilters,
    onFilterChange,
    handleFilterChange,
    handleFilterReset,
    openModal,
    filterOptions,
    filterOptionSelected,
    theme,
}) => {
    const getValueForFilter = filterName => get(selectedFilters, filterName);
    const isExpanded = get(filterOptions, 'isExpanded', false);
    const showFilterIcon = get(filterOptions, 'showFilterIcon', false);
    const [expanded, setExpandedFilters] = React.useState(isExpanded);
    const [openFilters, setOpenFilter] = React.useState({});
    const [inputValues, setInputValues] = React.useState({});

    useEffect(() => {
        setInputValues(
            filters.reduce((acc, filter) => {
                if (filter.type === FILTER_TYPE.INPUT) {
                    acc[filter.name] = get(selectedFilters, filter.name, '');
                }
                return acc;
            }, {})
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const timeout = setTimeout(() => {
            _forEach(inputValues, (value, name) => {
                onFilterChange(name, value);
            });
        }, 500);

        return () => clearTimeout(timeout);
    }, [inputValues]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCheckboxToggle = (event, name) => {
        setCheckedItems({ ...checkedItems, [name]: event.target.checked });
        onFilterChange(name, event.target.checked);
    };

    const filterList =
        filters &&
        filters.map(filter => {
            const {
                asyncCallbackRef = null,
                closeMenuOnSelect,
                disabled,
                isClearable = false,
                isMulti,
                isSearchable,
                name,
                options,
                title,
                type,
                placeholder,
                showOnMouseLeaveEvent = false,
                showYearDropdown = false,
                typeAheadFilterKey = null,
                useSearchIcon = false,
            } = filter;
            const asyncCallback = getHandler(actions, asyncCallbackRef);
            let filterNode = null;

            if (type === FILTER_TYPE.DROPDOWN) {
                filterNode = (
                    <FilterItem>
                        <DropdownFilter
                            asyncCallback={asyncCallback}
                            id={`dropdown_filter__${name}`}
                            closeMenuOnSelect={closeMenuOnSelect}
                            isMulti={isMulti}
                            isSearchable={isSearchable}
                            name={name}
                            onChange={onFilterChange}
                            options={options}
                            placeholder={placeholder}
                            value={getValueForFilter(name)}
                            onOpen={isOpen => setOpenFilter({ name, isOpen })}
                            isClearable={isClearable}
                        />
                    </FilterItem>
                );
            } else if (type === FILTER_TYPE.DATE_PICKER) {
                filterNode = (
                    <FilterItem>
                        <DatePicker
                            id={`datepicker_filter__${name}`}
                            onChange={value => onFilterChange(name, value)}
                            placeholder={placeholder}
                            selected={getValueForFilter(name)}
                            customInput={<CustomDatePickerInput />}
                            onCalendarOpen={() => setOpenFilter({ name, isOpen: true })}
                            onCalendarClose={() => setOpenFilter({ name, isOpen: false })}
                            showYearDropdown={showYearDropdown}
                            maxDate={filter.maxDate || null}
                            minDate={filter.minDate || null}
                        />
                    </FilterItem>
                );
            } else if (type === FILTER_TYPE.CHECKBOX) {
                filterNode = (
                    <CheckboxContainer>
                        <CheckboxStyled
                            checked={checkedItems[name] || selectedFilters[name]}
                            onChange={e => {
                                handleCheckboxToggle(e, name);
                            }}
                            type="checkbox"
                            icon={CheckboxEmptyIcon}
                            checkedIcon={CheckboxFilledIcon}
                        />
                        <TitleStyled as="div">{title}</TitleStyled>
                    </CheckboxContainer>
                );
            } else if (type === FILTER_TYPE.TYPE_AHEAD) {
                filterNode = (
                    <FilterItem>
                        <TypeaheadFilter
                            key={typeAheadFilterKey}
                            asyncCallback={asyncCallback}
                            disabled={disabled}
                            id={`typeahead_filter__${name}`}
                            name={name}
                            onChange={onFilterChange}
                            options={options}
                            placeholder={placeholder}
                            showOnMouseLeaveEvent={showOnMouseLeaveEvent}
                            value={getValueForFilter(name)}
                        />
                    </FilterItem>
                );
            } else if (type === FILTER_TYPE.INPUT) {
                filterNode = (
                    <FilterItem>
                        <InputContainer>
                            <InputStyled
                                disabled={disabled}
                                name={name}
                                onChange={e => {
                                    setInputValues({ ...inputValues, [name]: e.target.value });
                                }}
                                placeholder={placeholder}
                                value={inputValues[name] || ''}
                            />
                        </InputContainer>
                    </FilterItem>
                );
            } else if (type === FILTER_TYPE.ASYNC_SELECT) {
                filterNode = (
                    <FilterItem>
                        <AsyncSelect
                            cacheOptions
                            disabled={disabled}
                            isMulti
                            loadOptions={asyncCallback}
                            id={name}
                            onChange={(values, latest) => {
                                onFilterChange(latest.name, values);
                            }}
                            placeholder={placeholder}
                            useSearchIcon={useSearchIcon}
                            value={getValueForFilter(name)}
                        />
                    </FilterItem>
                );
            }

            if (title) {
                const content = (
                    <Typography
                        fontFamily="Roboto"
                        fontSize="0.688rem"
                        gutterBottom="0"
                        gutterTop="0"
                    >
                        {title}
                    </Typography>
                );

                const toolTipProps = {
                    content,
                    borderColor: DEFAULT_COLORS.BLACK,
                    borderRadius: '1.5rem',
                    icon: filterNode,
                    placement: 'top',
                    color: DEFAULT_COLORS.COMPONENTS_THE_GREY_STYLE,
                    open: openFilters.name === name && openFilters.isOpen,
                };

                if (type === !FILTER_TYPE.CHECKBOX) {
                    filterNode = (
                        <FilterItem>
                            <ContentTooltipPreview {...toolTipProps} />
                        </FilterItem>
                    );
                }
            }

            return (
                <div key={name} style={{ margin: '0.625rem' }}>
                    {filterNode}
                </div>
            );
        });

    const openFilterModal = () => {
        openModal(MODALS.FILTER, {
            handlers: actions,
            filters,
            onChangeFilter: ({ updatedFilterObject, name }) => {
                const updatedFilterValue = get(updatedFilterObject, name, null);
                onFilterChange(name, updatedFilterValue, updatedFilterObject);
            },
            onResetFilter: handleFilterReset,
            onSubmitFilter: handleFilterChange,
            value: selectedFilters,
        });
    };

    const onSubmitFilter = () => {
        handleFilterChange(selectedFilters);
    };

    return (
        <FilterContainer margin="0" width="100%">
            <Responsive size="md" value="less">
                <MobileFilter onClick={openFilterModal}>
                    <FilterToggle>
                        <IconStyled>
                            {showFilterIcon ? (
                                <FilterIcon />
                            ) : (
                                <GearIcon
                                    color={getThemeProps(
                                        'palette.primary.gradientColor',
                                        DEFAULT_COLORS.BLUE,
                                        {
                                            theme,
                                        }
                                    )}
                                    colorInner={getThemeProps(
                                        'palette.primary.text',
                                        DEFAULT_COLORS.BLUE,
                                        {
                                            theme,
                                        }
                                    )}
                                />
                            )}
                        </IconStyled>
                        <Typography type="tabTitle">Filters</Typography>
                    </FilterToggle>
                </MobileFilter>
            </Responsive>
            <Responsive size="md" value="greater">
                <FilterRow expanded={expanded} margin="0" width={`${expanded ? '100%' : ''}`}>
                    <Col
                        size={{ xs: 12, md: 2 }}
                        margin="0"
                        style={{ display: 'inherit', maxWidth: '10rem' }}
                    >
                        <FilterToggle onClick={() => setExpandedFilters(!expanded)}>
                            <IconStyled>
                                {showFilterIcon ? (
                                    <FilterIcon />
                                ) : (
                                    <GearIcon
                                        color={getThemeProps(
                                            'palette.primary.gradientColor',
                                            DEFAULT_COLORS.BLUE,
                                            {
                                                theme,
                                            }
                                        )}
                                        colorInner={getThemeProps(
                                            'palette.primary.text',
                                            DEFAULT_COLORS.BLUE,
                                            {
                                                theme,
                                            }
                                        )}
                                    />
                                )}
                            </IconStyled>
                            <Typography gutterBottom="0" gutterTop="0.125rem" type="tabTitle">
                                Filters
                            </Typography>
                        </FilterToggle>
                    </Col>

                    {expanded && (
                        <Col
                            display="flex"
                            size={{ xs: 12, md: 10 }}
                            padding="0"
                            style={{ alignItems: 'center' }}
                        >
                            <Row wrap="nowrap" width="100%">
                                <Col
                                    display="flex"
                                    size={{ xs: 12, md: 'auto' }}
                                    style={{
                                        flexWrap: 'wrap',
                                        margin: '-0.625rem',
                                        maxWidth: '83%',
                                    }}
                                    padding="0"
                                >
                                    {filterList}
                                </Col>
                                <Col padding="0 15px" size={{ xs: 12, md: 'auto' }}>
                                    <Row margin="0" wrap="nowrap">
                                        <Button
                                            margin="0 0.813rem 0 0"
                                            borderRadius="0"
                                            height="1.875rem"
                                            noMinWidth
                                            minHeight="38px"
                                            width="5.5rem"
                                            color={
                                                !filterOptionSelected ? 'lighter' : 'activeButton'
                                            }
                                            type="submit"
                                            onClick={onSubmitFilter}
                                        >
                                            <Typography>APPLY</Typography>
                                        </Button>
                                        <ResetLink
                                            type="link"
                                            onClick={() => {
                                                handleFilterReset();
                                                setCheckedItems({});
                                                setInputValues({});
                                            }}
                                            fontSize=".875rem"
                                            fontFamily="RobotoMono"
                                            gutterBottom="3px"
                                            style={{
                                                alignSelf: 'center',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            RESET
                                        </ResetLink>
                                        <ClosePanelContainer
                                            onClick={() => setExpandedFilters(!expanded)}
                                        >
                                            <ClosePanelIcon width="25" height="26" />
                                        </ClosePanelContainer>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                    )}
                </FilterRow>
            </Responsive>
        </FilterContainer>
    );
};

Filters.propTypes = {
    actions: objectOf(func),
    checkedItems: object,
    filters: arrayOf(
        shape({
            asyncCallbackRef: string,
            isMulti: bool,
            isSearchable: bool,
            maxDate: instanceOf(Date),
            minDate: instanceOf(Date),
            name: string.isRequired,
            options: arrayOf(
                shape({
                    label: string,
                    value: any.isRequired,
                }).isRequired
            ),
            title: string,
            type: oneOf([
                FILTER_TYPE.ASYNC_SELECT,
                FILTER_TYPE.CHECKBOX,
                FILTER_TYPE.DATE_PICKER,
                FILTER_TYPE.DROPDOWN,
                FILTER_TYPE.TOOLTIP,
                FILTER_TYPE.TYPE_AHEAD,
                FILTER_TYPE.INPUT,
            ]).isRequired,
        }).isRequired
    ).isRequired,
    filterOptions: shape({
        isExpanded: bool,
    }).isRequired,
    selectedFilters: object.isRequired,
    onFilterChange: func.isRequired,
    openModal: func.isRequired,
    handleFilterChange: func.isRequired,
    handleFilterReset: func.isRequired,
    filterOptionSelected: bool,
    setCheckedItems: func,
    theme: object.isRequired,
};

Filters.defaultProps = {
    actions: {},
    checkedItems: {},
    filterOptionSelected: false,
    setCheckedItems: () => {},
};

const mapDispatchToProps = {
    openModal: uiActions.openModal,
};

export { Filters as FiltersUnwrapped };
export default withTheme(connect(null, mapDispatchToProps)(Filters));
